import { throwError } from "@/lib/throwError";
import { del, get, post, put } from "@/services/axiosUitils";
import {
  LetterHead,
  LetterHeadList,
  LetterHeadPayload,
} from "@/utils/types/latterHead";

const basePath = "/print_formats/letter_heads";

class LetterHeadService {
  getAllLetterHeads = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }): Promise<LetterHeadList> => {
    return await get<LetterHeadList>(`${basePath}/`, {
      params: {
        page,
        page_size: pageSize,
      },
    });
  };

  getLetterHeadById = async (id: string): Promise<LetterHead> => {
    if (!id) {
      throwError("ID is required", "LetterHeadService");
    }
    return await get<LetterHead>(`${basePath}/${id}/`);
  };

  createLetterHead = async (payload: LetterHeadPayload): Promise<any> => {
    if (!payload) {
      throwError("Payload is required", "LetterHeadService");
    }

    // If image upload is needed, use FormData
    const useFormData =
      payload.header_type === "image" || payload.footer_type === "image";

    if (useFormData) {
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("header_type", payload.header_type);
      formData.append("footer_type", payload.footer_type);
      formData.append("is_default", String(payload.is_default));

      if (payload.header_type === "image" && payload.header_image) {
        formData.append("header_image", payload.header_image as File);
        formData.append(
          "header_image_width",
          String(payload.header_image_width ?? 240)
        );
        formData.append(
          "header_image_height",
          String(payload.header_image_height ?? 80)
        );
        formData.append(
          "header_image_align",
          payload.header_image_align ?? "left"
        );
      } else if (payload.header_type === "html" && payload.header_html) {
        formData.append("header_html", payload.header_html);
      }

      if (payload.footer_type === "image" && payload.footer_image) {
        formData.append("footer_image", payload.footer_image as File);
        formData.append(
          "footer_image_width",
          String(payload.footer_image_width ?? 240)
        );
        formData.append(
          "footer_image_height",
          String(payload.footer_image_height ?? 80)
        );
        formData.append(
          "footer_image_align",
          payload.footer_image_align ?? "right"
        );
      } else if (payload.footer_type === "html" && payload.footer_html) {
        formData.append("footer_html", payload.footer_html);
      }

      return await post(`${basePath}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    return await post(`${basePath}/`, payload);
  };

  updateLetterHead = async (
    id: string,
    payload: LetterHeadPayload
  ): Promise<any> => {
    if (!id || !payload) {
      throwError("ID and payload are required", "LetterHeadService");
    }

    // If image upload is needed, use FormData
    const useFormData =
      payload.header_type === "image" || payload.footer_type === "image";

    if (useFormData) {
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("header_type", payload.header_type);
      formData.append("footer_type", payload.footer_type);
      formData.append("is_default", String(payload.is_default));

      if (payload.header_type === "image" && payload.header_image) {
        formData.append("header_image", payload.header_image as File);
        formData.append(
          "header_image_width",
          String(payload.header_image_width ?? 240)
        );
        formData.append(
          "header_image_height",
          String(payload.header_image_height ?? 80)
        );
        formData.append(
          "header_image_align",
          payload.header_image_align ?? "left"
        );
      } else if (payload.header_type === "html" && payload.header_html) {
        formData.append("header_html", payload.header_html);
      }

      if (payload.footer_type === "image" && payload.footer_image) {
        formData.append("footer_image", payload.footer_image as File);
        formData.append(
          "footer_image_width",
          String(payload.footer_image_width ?? 240)
        );
        formData.append(
          "footer_image_height",
          String(payload.footer_image_height ?? 80)
        );
        formData.append(
          "footer_image_align",
          payload.footer_image_align ?? "right"
        );
      } else if (payload.footer_type === "html" && payload.footer_html) {
        formData.append("footer_html", payload.footer_html);
      }

      return await put(`${basePath}/${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }

    return await put(`${basePath}/${id}/`, payload);
  };

  deleteLetterHead = async (id: string): Promise<any> => {
    if (!id) {
      throwError("ID is required", "LetterHeadService");
    }
    return await del(`${basePath}/${id}/`);
  };

  previewLetterHeadHTML = async (letterHeadId: string): Promise<any> => {
    if (!letterHeadId) {
      throwError("Letter Head ID is required", "LetterHeadService");
    }
    return await get(`/print_formats/letter_heads/${letterHeadId}/preview/`);
  };

  renderLetterHeadPDF = async (letterHeadId: string): Promise<Blob> => {
    if (!letterHeadId) {
      throwError("Letter Head ID is required", "LetterHeadService");
    }
    return await get(
      `/print_formats/letter_heads/${letterHeadId}/render_pdf/`,
      {
        responseType: "blob",
      }
    );
  };
}

const instance = new LetterHeadService();
export default instance;
